import dayjs from 'dayjs';
import type { BlockedArea, ErrorMessage, PageContext, ScreenMessage, UnmetClause, UnresolvedError } from '@api/assistant/assistantApi';
import { createLogger } from '@utils/logger';
import { ErrorEntry, getBlockedAreas, getErrorEntries, getMessageEntries, getRouteTree, MessageEntry, pruneExpired } from './pageContextStore';
import { resolveRoute } from './routePattern';

const log = createLogger('assistantPageContext');

const MAX_MESSAGES = 10;
const MAX_BLOCKED = 20;
const MAX_NAVIGATION = 15;
const MAX_INSTANCES = 10;
const MAX_ERRORS = 20;
const MAX_ERROR_MESSAGES = 5;
const MAX_PARAMS = 5;
const MAX_STRING = 200;
const MAX_TEXT = 500;
export const MAX_BYTES = 8 * 1024;

/** Where and when the snapshot is taken. Injectable so the builder can be tested outside a browser. */
export interface SnapshotEnv {
  now: number;
  pathname: string;
  search: string;
  appRelease: string | null;
}

const currentEnv = (): SnapshotEnv => ({
  now: Date.now(),
  pathname: window.location.pathname,
  search: window.location.search,
  appRelease: import.meta.env.VITE_COMMIT_SHA || null,
});

const clip = (value: string, max = MAX_STRING) => (value.length > max ? value.slice(0, max) : value);
const clipOrNull = (value: string | null | undefined, max = MAX_STRING) => (value == null ? null : clip(value, max));
const clipParams = (params: string[] | undefined) => (params ?? []).slice(0, MAX_PARAMS).map(p => clip(String(p)));
const iso = (ms: number) => dayjs(ms).format();

const toScreenMessage = (entry: MessageEntry): ScreenMessage => ({
  key: clip(entry.data.key),
  params: clipParams(entry.data.params),
  code: clipOrNull(entry.data.code),
  variant: entry.data.variant,
  on_screen: entry.endedAt === null,
  last_seen_at: entry.endedAt === null ? null : iso(entry.endedAt),
});

const toErrorMessage = (message: ErrorMessage): ErrorMessage => ({
  key: clipOrNull(message.key),
  text: clipOrNull(message.text, MAX_TEXT),
  params: clipParams(message.params),
  scope: clip(message.scope ?? ''),
  path: clip(message.path ?? ''),
});

const toUnresolvedError = (entry: ErrorEntry): UnresolvedError => ({
  method: clip(entry.data.method),
  http_method: clip(entry.data.http_method),
  status: entry.data.status,
  error_type: clipOrNull(entry.data.error_type),
  error_code: clipOrNull(entry.data.error_code),
  first_at: iso(entry.firstAt),
  last_at: iso(entry.lastAt),
  count: entry.count,
  trc_uuid: clipOrNull(entry.data.trc_uuid),
  messages: entry.data.messages.slice(0, MAX_ERROR_MESSAGES).map(toErrorMessage),
});

const toUnmetClause = (clause: UnmetClause): UnmetClause => ({
  type: clause.type,
  label: clipOrNull(clause.label),
  any_of: clause.any_of?.map(name => clip(name)) ?? null,
  rule: clipOrNull(clause.rule),
  description: clipOrNull(clause.description),
  by_design: clause.by_design,
});

const toBlockedArea = (area: BlockedArea): BlockedArea => ({
  id: clip(area.id),
  instances: area.instances?.slice(0, MAX_INSTANCES).map(instance => clip(instance)) ?? null,
  label: clipOrNull(area.label),
  description: clipOrNull(area.description),
  note: clipOrNull(area.note),
  blocked: area.blocked,
  count: area.count,
  unmet: area.unmet.map(toUnmetClause),
});

const byteSize = (value: unknown) => new TextEncoder().encode(JSON.stringify(value)).length;

/**
 * Shrinks the snapshot to MAX_BYTES, least useful first: hidden menu entries, the oldest errors, blocked
 * areas, messages that have left the screen, then on-screen ones. The lists are already ordered so that
 * the least useful entry is always the last one.
 */
const fitToSize = (context: PageContext): PageContext => {
  while (byteSize(context) > MAX_BYTES) {
    if (context.blocked_navigation.length) context.blocked_navigation.pop();
    else if (context.unresolved_errors.length) context.unresolved_errors.pop();
    else if (context.blocked.length) context.blocked.pop();
    else if (context.messages.length) context.messages.pop();
    else break;
  }
  return context;
};

/** The snapshot attached to a chat request: taken when the user sends, never read again later. */
export const buildPageContext = (env: SnapshotEnv = currentEnv()): PageContext => {
  pruneExpired(env.now);

  // On screen first (newest first), then the ones that have left the screen, most recently gone first.
  const messages = getMessageEntries()
    .sort((a, b) => {
      if ((a.endedAt === null) !== (b.endedAt === null)) return a.endedAt === null ? -1 : 1;
      return a.endedAt === null ? b.startedAt - a.startedAt : (b.endedAt ?? 0) - (a.endedAt ?? 0);
    })
    .slice(0, MAX_MESSAGES)
    .map(toScreenMessage);

  const unresolvedErrors = getErrorEntries()
    .sort((a, b) => b.lastAt - a.lastAt)
    .slice(0, MAX_ERRORS)
    .map(toUnresolvedError);

  const route = resolveRoute(getRouteTree(), env.pathname);
  const { blocked, navigation } = getBlockedAreas();

  return fitToSize({
    version: 1,
    captured_at: iso(env.now),
    app_release: clipOrNull(env.appRelease),
    impersonating: !!new URLSearchParams(env.search).get('runAs'),
    route: route && { pattern: clip(route.pattern), params: Object.fromEntries(Object.entries(route.params).map(([k, v]) => [clip(k), clip(v)])) },
    messages,
    blocked: blocked.slice(0, MAX_BLOCKED).map(toBlockedArea),
    blocked_navigation: navigation.slice(0, MAX_NAVIGATION).map(toBlockedArea),
    unresolved_errors: unresolvedErrors,
  });
};

/** Never lets a bug here stop the question from being sent: without a snapshot the request goes out bare. */
export const safeBuildPageContext = (): PageContext | undefined => {
  try {
    return buildPageContext();
  } catch (error) {
    log.error('Building the assistant page context failed; sending the question without it', error);
    return undefined;
  }
};
